import axios from "axios";
import { REACT_APP_DOMAIN_URL } from "features/shared/constants";
import {
  CreateGiftCertificateParam,
  CreateStoreGroupParam,
  formListParam,
  GiftCertificateFilters,
  MoneyUpdateEdit,
  SetStoreStatusParam,
  StoreStatusDashboardStats,
  StoreStatusData,
  StoreStatusFilters,
  UpdateGCStatusParam,
  UserStore,
} from "../core/ms6.param";
import { DailyAttendanceTableData } from "../core/domain/DailyAttendanceTableData.model";
import { AttendanceStaticData } from "../core/domain/DailyStaticData.model";
import { DailyIncidentTableData } from "../core/domain/DailyIncidentTableData.model";
import { IncidentStaticData } from "../core/domain/DailyStaticIncidentData.model";
import { DailyEfficiencyTableData } from "../core/domain/DailyEfficiencyTableData.model";
import { DailyInventoryTableData } from "../core/domain/DailyInventoryTableData.model";
import { DailySvrTableData } from "../core/domain/DailySvrTableData.model";
import { DailyProduct } from "../core/domain/DailyProductData.model";
import {
  SortField,
  SortDirection,
} from "../presentation/slices/store-status.slice";

/**
 * Response data formListSubmitResponse
 */
export interface formListSubmitResponse {
  data: {
    message: string;
  };
}

export interface UserStores6MSResponse {
  data: {
    data: Array<{
      store_id: string;
      store_name: string;
    }>;
    message: string;
  };
}

/**
 * Response data formListSubmitResponse
 */
export interface dailyAttendanceFieldResponse {
  data: {
    data: any; // change this with new model
    message: string;
  };
}

export interface dailyIncidentFieldResponse {
  data: {
    data: any; // change this with new model
    message: string;
  };
}

export interface dailyInventoryFieldResponse {
  data: {
    data: any; //change this with new model
    message: string;
  };
}

export interface dailyEfficiencyFieldResponse {
  data: {
    data: any; //change this with new model
    message: string;
  };
}

export interface dailySvrFieldResponse {
  data: {
    data: any; //change this with new model
    message: string;
  };
}

// Table Data for viewing Attendance Table Records
export interface DailyAttendanceTableDataResponse {
  data: {
    data: DailyAttendanceTableData;
    message: string;
  };
}

export interface DailyStaticDataResponse {
  data: {
    data: AttendanceStaticData;
    message: string;
  };
}

// Table data for viewing Incident records
export interface DailyIncidentTableDataResponse {
  data: {
    data: DailyIncidentTableData;
    message: string;
  };
}

export interface DailyIncidentDataResponse {
  data: {
    data: IncidentStaticData;
    message: string;
  };
}

export interface DailyProductResponse {
  data: {
    data: DailyProduct;
    message: string;
  };
}

// Table data for viewing Inventory records
export interface DailyInventoryTableDataResponse {
  data: {
    data: DailyInventoryTableData;
    message: string;
  };
}

// Table Data for viewing efficiency records
export interface DailyEfficiencyTableDataResponse {
  data: {
    data: DailyEfficiencyTableData;
    message: string;
  };
}

// Table Data for viewing Svr records
export interface DailySvrTableDataResponse {
  data: {
    data: DailySvrTableData;
    message: string;
  };
}

// Request response data for updating edit on svr data
export interface updateEditSvrResponse {
  data: {
    message: string;
  };
}

/**
 * Axios / repository that communicates with backend daily_attendance endpoint for post/submission
 */
export function formListRepository(
  param: formListParam
): Promise<formListSubmitResponse> {
  return axios.post(
    `${REACT_APP_DOMAIN_URL}api/form_6ms/daily_attendance`,
    param,
    {
      withCredentials: true,
    }
  );
}

/**
 * Axios / repository that communicates with backend daily_attendance endpoint for  get/fetching data
 */
export function dailyAttendanceFieldRepository(): Promise<dailyAttendanceFieldResponse> {
  return axios.get(`${REACT_APP_DOMAIN_URL}api/form_6ms/daily_attendance`, {
    withCredentials: true,
  });
}

export function dailyIncidentFieldRepository(): Promise<dailyIncidentFieldResponse> {
  return axios.get(`${REACT_APP_DOMAIN_URL}api/form_6ms/daily_incident`, {
    withCredentials: true,
  });
}

export function dailyInventoryFieldResponse(): Promise<dailyInventoryFieldResponse> {
  return axios.get(`${REACT_APP_DOMAIN_URL}api/form_6ms/daily_inventory`, {
    withCredentials: true,
  });
}

export function dailyEfficiencyFieldResponse(): Promise<dailyEfficiencyFieldResponse> {
  return axios.get(`${REACT_APP_DOMAIN_URL}api/form_6ms/daily_efficiency`, {
    withCredentials: true,
  });
}

export function dailySvrFieldResponse(): Promise<dailySvrFieldResponse> {
  return axios.get(`${REACT_APP_DOMAIN_URL}api//DailySvr/generateSvr`, {
    withCredentials: true,
  });
}

export function DailyAttendanceTableDataRepository(): Promise<DailyAttendanceTableDataResponse> {
  return axios.get(
    `${REACT_APP_DOMAIN_URL}api/form_6ms/daily-attendance-data`,
    {
      withCredentials: true,
    }
  );
}

export function DailyIncidentTableDataRepository(): Promise<DailyIncidentTableDataResponse> {
  return axios.get(`${REACT_APP_DOMAIN_URL}api/form_6ms/daily-incident-data`, {
    withCredentials: true,
  });
}

export function DailyInventoryTableDataRepository(): Promise<DailyInventoryTableDataResponse> {
  return axios.get(`${REACT_APP_DOMAIN_URL}api/form_6ms/daily-inventory-data`, {
    withCredentials: true,
  });
}

export function DailyEfficiencyTableDataRepository(): Promise<DailyEfficiencyTableDataResponse> {
  return axios.get(
    `${REACT_APP_DOMAIN_URL}api/form_6ms/daily-efficiency-data`,
    {
      withCredentials: true,
    }
  );
}

// For Getting Table Data Records
export function DailySvrTableDataRepository(): Promise<DailySvrTableDataResponse> {
  return axios.get(`${REACT_APP_DOMAIN_URL}api/form_6ms/generateSvr`, {
    withCredentials: true,
  });
}

export function DailyStaticDataRepository(
  storeId?: string
): Promise<DailyStaticDataResponse> {
  const url = storeId
    ? `${REACT_APP_DOMAIN_URL}api/form_6ms/daily_attendance?store_id=${storeId}`
    : `${REACT_APP_DOMAIN_URL}api/form_6ms/daily_attendance`;

  return axios.get(url, {
    withCredentials: true,
  });
}

export function getUserStores6MSRepository(): Promise<UserStores6MSResponse> {
  return axios.get(`${REACT_APP_DOMAIN_URL}api/form_6ms/User_stores`, {
    withCredentials: true,
  });
}

// get product table
export function DailyProductRepository(): Promise<DailyProductResponse> {
  return axios.get(
    `${REACT_APP_DOMAIN_URL}api/form_6ms/Daily_product_dropdown`,
    {
      withCredentials: true,
    }
  );
}

// update edit on svr
export function updateEditSvrRepository(
  param: MoneyUpdateEdit
): Promise<updateEditSvrResponse> {
  return axios.put(
    `${REACT_APP_DOMAIN_URL}api/money/svr/edit/edit-svr-data`,
    param,
    {
      withCredentials: true,
    }
  );
}

export interface UserStoresResponse {
  data: {
    data: Array<{ id: number; name: string; address: string }>;
    message: string;
  };
}

export function getUserStoresRepository(): Promise<UserStoresResponse> {
  return axios.get(`${REACT_APP_DOMAIN_URL}api/gift-certificate/user-stores`, {
    withCredentials: true,
  });
}

// Gift Certificate Response Interfaces
export interface GiftCertificateDashboardResponse {
  data: {
    data: {
      stats: {
        by_status: Record<string, number>;
        by_type: Record<string, number>;
        recent_week: number;
      };
      last_created: {
        customer: any;
        sponsorship: any;
        support: any;
      };
    };
    message: string;
  };
}

export interface DropdownDataResponse {
  data: {
    data: {
      stores: Array<{ id: number; name: string; address: string }>;
      companies: Array<{ id: number; name: string }>;
      store_groups: Array<any>;
    };
    message: string;
  };
}

export interface CreateGiftCertificateResponse {
  data: {
    data: {
      created_count: number;
      serial_range: {
        start: string;
        end: string;
      };
    };
    message: string;
  };
}

export interface GiftCertificateListResponse {
  data: {
    data: Array<any>;
    can_see_created_gcs: boolean;
    message: string;
  };
}

export interface UpdateGCStatusResponse {
  data: {
    message: string;
  };
}

export interface GCDetailsResponse {
  data: {
    data: {
      certificate: any;
      history: Array<any>;
    };
    message: string;
  };
}

export interface StoreGroupsResponse {
  data: {
    data: Array<any>;
    message: string;
  };
}

export interface CreateStoreGroupResponse {
  data: {
    data: {
      group_id: number;
    };
    message: string;
  };
}

// Add this to your existing repository
export interface AdminCheckResponse {
  data: {
    status: string;
    is_admin: boolean;
    message?: string;
  };
}

export interface UserPermissionsResponse {
  data: {
    status: string;
    data: {
      is_admin: boolean;
      is_stock_clerk: boolean;
      can_approve_status: boolean;
      can_see_redeem_approvals?: boolean;
      can_approve_redemption?: boolean;
      stock_permissions: Array<{
        permission_id: number;
        permission_name: string;
      }>;
    };
  };
}

export interface ValidStatusesResponse {
  data: {
    status: string;
    data: string[];
  };
}

export function getActiveStoresRepository(): Promise<UserStoresResponse> {
  return axios.get(
    `${REACT_APP_DOMAIN_URL}api/gift-certificate/get-active-stores`,
    {
      withCredentials: true,
    }
  );
}

export function checkAdminAccessRepository(): Promise<{
  data: {
    status: string;
    is_admin: boolean;
    is_gc: boolean;
    message?: string;
  };
}> {
  return axios.get(`${REACT_APP_DOMAIN_URL}api/gift-certificate/check-admin`, {
    withCredentials: true,
  });
}

export function getCompanyUserPermissionsRepository(): Promise<{
  data: {
    status: string;
    data: {
      is_admin: boolean;
      is_gc: boolean;
      can_view: boolean;
      can_edit: boolean;
      can_delete: boolean;
    };
  };
}> {
  return axios.get(
    `${REACT_APP_DOMAIN_URL}api/company-management/get-user-permissions`,
    {
      withCredentials: true,
    }
  );
}

// Gift Certificate Repository Functions
export function getDashboardDataRepository(): Promise<GiftCertificateDashboardResponse> {
  return axios.get(`${REACT_APP_DOMAIN_URL}api/gift-certificate/dashboard`, {
    withCredentials: true,
  });
}

export function getDropdownDataRepository(): Promise<DropdownDataResponse> {
  return axios.get(
    `${REACT_APP_DOMAIN_URL}api/gift-certificate/dropdown-data`,
    {
      withCredentials: true,
    }
  );
}

export function getStatusTooltipsRepository(): Promise<{
  data: {
    status: string;
    data: Record<
      string,
      {
        tooltip_text: string;
        next_status: string | null;
      }
    >;
  };
}> {
  return axios.get(
    `${REACT_APP_DOMAIN_URL}api/gift-certificate/get-status-tooltips`,
    {
      withCredentials: true,
    }
  );
}

export function createGiftCertificatesRepository(
  param: CreateGiftCertificateParam
): Promise<CreateGiftCertificateResponse> {
  return axios.post(
    `${REACT_APP_DOMAIN_URL}api/gift-certificate/create`,
    param,
    {
      withCredentials: true,
    }
  );
}

export function getGiftCertificatesRepository(
  filters: GiftCertificateFilters = {}
): Promise<GiftCertificateListResponse> {
  const queryParams = new URLSearchParams();
  Object.keys(filters).forEach((key) => {
    if (filters[key as keyof GiftCertificateFilters]) {
      queryParams.append(key, filters[key as keyof GiftCertificateFilters]!);
    }
  });

  const url = queryParams.toString()
    ? `${REACT_APP_DOMAIN_URL}api/gift-certificate/list?${queryParams}`
    : `${REACT_APP_DOMAIN_URL}api/gift-certificate/list`;

  return axios.get(url, {
    withCredentials: true,
  });
}

export function updateGCStatusRepository(
  param: UpdateGCStatusParam & { redemption_date?: string }
): Promise<UpdateGCStatusResponse> {
  return axios.post(
    `${REACT_APP_DOMAIN_URL}api/gift-certificate/update-status`,
    param,
    {
      withCredentials: true,
    }
  );
}

export function bulkApproveRequestsRepository(approvalData: {
  request_ids: number[];
  review_notes?: string;
}): Promise<UpdateGCStatusResponse> {
  return axios.post(
    `${REACT_APP_DOMAIN_URL}api/gift-certificate/bulk-approve-requests`,
    approvalData,
    {
      withCredentials: true,
    }
  );
}

export function getGCDetailsRepository(
  gcId: number
): Promise<GCDetailsResponse> {
  return axios.get(
    `${REACT_APP_DOMAIN_URL}api/gift-certificate/details/${gcId}`,
    {
      withCredentials: true,
    }
  );
}

export function createStoreGroupRepository(groupData: {
  group_name: string;
  store_ids: number[];
}): Promise<CreateStoreGroupResponse> {
  return axios.post(
    `${REACT_APP_DOMAIN_URL}api/gift-certificate/create-store-group`,
    groupData,
    {
      withCredentials: true,
      headers: {
        "Content-Type": "application/json", // Make sure this is set
      },
    }
  );
}

export function getStoreGroupsRepository(): Promise<StoreGroupsResponse> {
  return axios.get(`${REACT_APP_DOMAIN_URL}api/gift-certificate/store-groups`, {
    withCredentials: true,
  });
}

export function deleteGiftCertificateRepository(
  gcId: number
): Promise<UpdateGCStatusResponse> {
  return axios.delete(
    `${REACT_APP_DOMAIN_URL}api/gift-certificate/delete/${gcId}`,
    {
      withCredentials: true,
    }
  );
}

export function getStoreGroupsDetailedRepository(): Promise<StoreGroupsResponse> {
  return axios.get(
    `${REACT_APP_DOMAIN_URL}api/gift-certificate/store-groups-detailed`,
    {
      withCredentials: true,
    }
  );
}

export function deleteStoreGroupRepository(
  groupId: number
): Promise<UpdateGCStatusResponse> {
  return axios.delete(
    `${REACT_APP_DOMAIN_URL}api/gift-certificate/delete-store-group/${groupId}`,
    {
      withCredentials: true,
    }
  );
}

export function updateStoreGroupRepository(groupData: {
  group_id: number;
  group_name: string;
  store_ids: number[];
}): Promise<CreateStoreGroupResponse> {
  return axios.post(
    `${REACT_APP_DOMAIN_URL}api/gift-certificate/update-store-group`,
    groupData,
    {
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
}

// Add these repository functions
export function requestStatusChangeRepository(requestData: {
  gc_id: number;
  status: string;
  notes?: string;
  redeemed_at_store_id?: number;
  redemption_date?: string;
}): Promise<UpdateGCStatusResponse> {
  return axios.post(
    `${REACT_APP_DOMAIN_URL}api/gift-certificate/request-status-change`,
    requestData,
    {
      withCredentials: true,
    }
  );
}

export function getPendingRequestsRepository(): Promise<any> {
  return axios.get(
    `${REACT_APP_DOMAIN_URL}api/gift-certificate/pending-requests`,
    {
      withCredentials: true,
    }
  );
}

export function reviewStatusRequestRepository(reviewData: {
  request_id: number;
  action: "approved" | "rejected";
  review_notes?: string;
}): Promise<UpdateGCStatusResponse> {
  return axios.post(
    `${REACT_APP_DOMAIN_URL}api/gift-certificate/review-status-request`,
    reviewData,
    {
      withCredentials: true,
    }
  );
}

export function getUserPermissionsRepository(): Promise<UserPermissionsResponse> {
  return axios.get(
    `${REACT_APP_DOMAIN_URL}api/gift-certificate/user-permissions`,
    {
      withCredentials: true,
    }
  );
}

export function getValidNextStatusesRepository(
  currentStatus: string
): Promise<ValidStatusesResponse> {
  return axios.get(
    `${REACT_APP_DOMAIN_URL}api/gift-certificate/valid-next-statuses/${currentStatus}`,
    {
      withCredentials: true,
    }
  );
}

export function checkPendingApprovalRepository(gcId: number): Promise<any> {
  return axios.get(
    `${REACT_APP_DOMAIN_URL}api/gift-certificate/check-pending-approval/${gcId}`,
    {
      withCredentials: true,
    }
  );
}

export interface AvailableStatusesResponse {
  data: {
    status: string;
    data: string[];
  };
}

export function getAvailableStatusesRepository(): Promise<AvailableStatusesResponse> {
  return axios.get(
    `${REACT_APP_DOMAIN_URL}api/gift-certificate/available-statuses`,
    {
      withCredentials: true,
    }
  );
}

// Simplified Company Management Interfaces
export interface Company {
  id: number;
  name: string;
  address?: string;
  contact_number?: string;
  gc_count: number;
}

export interface CreateCompanyParam {
  name: string;
  address?: string;
  contact_number?: string;
}

export interface CompanyListResponse {
  data: {
    status: string;
    data: Company[];
  };
}

export interface CompanyDetailsResponse {
  data: {
    status: string;
    data: Company;
  };
}

export interface CompanyActionResponse {
  data: {
    status: string;
    message: string;
    company_id?: number;
  };
}

// Company Management Repository Functions
export function getCompaniesRepository(): Promise<CompanyListResponse> {
  return axios.get(`${REACT_APP_DOMAIN_URL}api/company-management/list`, {
    withCredentials: true,
  });
}

export function createCompanyRepository(
  companyData: CreateCompanyParam
): Promise<CompanyActionResponse> {
  return axios.post(
    `${REACT_APP_DOMAIN_URL}api/company-management/create`,
    companyData,
    {
      withCredentials: true,
    }
  );
}

export function getCompanyDetailsRepository(
  companyId: number
): Promise<CompanyDetailsResponse> {
  return axios.get(
    `${REACT_APP_DOMAIN_URL}api/company-management/details/${companyId}`,
    {
      withCredentials: true,
    }
  );
}

export function updateCompanyRepository(
  companyId: number,
  companyData: CreateCompanyParam
): Promise<CompanyActionResponse> {
  return axios.put(
    `${REACT_APP_DOMAIN_URL}api/company-management/update/${companyId}`,
    companyData,
    {
      withCredentials: true,
    }
  );
}

export function deleteCompanyRepository(
  companyId: number
): Promise<CompanyActionResponse> {
  return axios.delete(
    `${REACT_APP_DOMAIN_URL}api/company-management/delete/${companyId}`,
    {
      withCredentials: true,
    }
  );
}

// Store Status Response Interfaces
export interface UserStoresStatusResponse {
  data: {
    status: "success" | "error";
    data: UserStore[];
    is_admin: boolean;
    user_type: string | null;
    has_permission: boolean;
    message?: string; // Only present in error cases
  };
}

export interface SetStoreStatusResponse {
  data: {
    status: string;
    message: string;
  };
}

export interface StoreStatusListResponse {
  data: {
    status: string;
    data: StoreStatusData[];
  };
}

export interface StoreStatusDashboardResponse {
  data: {
    status: string;
    data: StoreStatusDashboardStats;
  };
}

export interface StoreStatusHistoryResponse {
  data: {
    status: string;
    data: Array<{
      id: number;
      store_id: number;
      status: string;
      status_date: string;
      formatted_date: string;
      notes?: string;
    }>;
  };
}

// Store Status Repository Functions
export function getUserStoresStatusRepository(): Promise<UserStoresStatusResponse> {
  return axios.get(
    `${REACT_APP_DOMAIN_URL}api/daily-store-status/get-user-stores`,
    {
      withCredentials: true,
    }
  );
}

export function setStoreStatusRepository(
  param: SetStoreStatusParam
): Promise<SetStoreStatusResponse> {
  return axios.post(
    `${REACT_APP_DOMAIN_URL}api/daily-store-status/set-status`,
    param,
    {
      withCredentials: true,
    }
  );
}

export function getStoreStatusByDateRepository(
  filters: StoreStatusFilters = {}
): Promise<StoreStatusListResponse> {
  const queryParams = new URLSearchParams();
  Object.keys(filters).forEach((key) => {
    if (filters[key as keyof StoreStatusFilters]) {
      queryParams.append(key, filters[key as keyof StoreStatusFilters]!);
    }
  });

  const url = queryParams.toString()
    ? `${REACT_APP_DOMAIN_URL}api/daily-store-status/get-status-by-date?${queryParams}`
    : `${REACT_APP_DOMAIN_URL}api/daily-store-status/get-status-by-date`;

  return axios.get(url, {
    withCredentials: true,
  });
}

export function getStoreStatusDashboardRepository(): Promise<StoreStatusDashboardResponse> {
  return axios.get(
    `${REACT_APP_DOMAIN_URL}api/daily-store-status/get-dashboard-data`,
    {
      withCredentials: true,
    }
  );
}

export function getStoreStatusHistoryRepository(
  storeId: number
): Promise<StoreStatusHistoryResponse> {
  return axios.get(
    `${REACT_APP_DOMAIN_URL}api/daily-store-status/get-store-history?store_id=${storeId}`,
    {
      withCredentials: true,
    }
  );
}

export function getGCAttachmentsRepository(gcId: number): Promise<{
  data: {
    status: string;
    data: Array<{
      id: number;
      file_name: string;
      file_path: string;
      file_type: string;
      file_size: number;
      uploaded_by: number;
      uploaded_at: string;
      first_name: string;
      last_name: string;
    }>;
  };
}> {
  return axios.get(
    `${REACT_APP_DOMAIN_URL}api/gift-certificate/attachments/${gcId}`,
    {
      withCredentials: true,
    }
  );
}

// Add this to your existing repository functions
export function getAllStoreStatusesRepository(
  params: {
    page?: number;
    pageSize?: number;
    sortField?: SortField;
    sortDirection?: SortDirection;
  } = {}
): Promise<any> {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append("page", params.page.toString());
  if (params.pageSize)
    queryParams.append("pageSize", params.pageSize.toString());
  if (params.sortField) queryParams.append("sortField", params.sortField);
  if (params.sortDirection)
    queryParams.append("sortDirection", params.sortDirection);

  const url = queryParams.toString()
    ? `${REACT_APP_DOMAIN_URL}api/daily-store-status/get-all-statuses?${queryParams}`
    : `${REACT_APP_DOMAIN_URL}api/daily-store-status/get-all-statuses`;

  return axios.get(url, {
    withCredentials: true,
  });
}
